import { numFont } from '../../../utils/fonts';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Paper, Box, Button, Typography, Stack, Avatar } from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import { useTranslation } from 'react-i18next';

import { getOrganizationId, useUserStore } from '../../../store';
import { useTimeclockStore, selectWoSession } from '../../../storeTimeclock';
import { useClockController } from './useClockController';
import WorkOrderPicker from './WorkOrderPicker';
import PWaitDialog from './dialogs/PWaitDialog';
import { TOKENS, LiveDot, hmShort, useNow, elapsedSeconds } from './worktimeLib';
import { calcJobsLaborTime } from '../../../utils/laborTime';

import { can } from '../../../utils/access';
const initials = (name) =>
  (name || '?')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

// Same rules as the Jobs card's labor time total: an hourly line's quantity already is its
// hours, so multiplying it by the job's estimate would count the estimate twice.
function estimateHoursFromJobs(maintenance) {
  const { minutes } = calcJobsLaborTime(maintenance?.jobs);
  return minutes > 0 ? minutes / 60 : null;
}

/**
 * "Työaika" panel on the maintenance detail page. Reads the per-order clock summary
 * (live + past contributors + logged total). Estimate is client-side from the order's jobs
 * (see calcJobsLaborTime). Polled slowly (mount + focus + after actions + 60 s) —
 * the live elapsed ticks locally.
 */
export default function WorkOrderClockPanel({ maintenance, onMaintenanceChanged }) {
  const { t, i18n } = useTranslation();
  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'fi-FI') : '';
  const config = useTimeclockStore((s) => s.config);
  const fetchConfig = useTimeclockStore((s) => s.fetchConfig);
  const woSession = useTimeclockStore(selectWoSession);
  const isAdmin = useUserStore((s) => can(s.currentOrganization, 'timeclock.settings'));
  const [summary, setSummary] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pwaitDismissed, setPwaitDismissed] = useState(false);
  const nowMs = useNow();

  const fetchSummary = useCallback(async () => {
    const orgId = getOrganizationId();
    if (!orgId || !maintenance?.id) return;
    try {
      const res = await axios.get(`/api/scheduling/timeclock/maintenance/${maintenance.id}/?organization=${orgId}`);
      setSummary(res.data);
    } catch {
      /* panel is best-effort; leave previous summary */
    }
  }, [maintenance?.id]);

  const ctrl = useClockController({
    onMaintenanceChanged: () => {
      if (onMaintenanceChanged) onMaintenanceChanged();
      fetchSummary();
    },
  });

  useEffect(() => {
    if (!config) fetchConfig();
  }, [config, fetchConfig]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    const id = setInterval(fetchSummary, 60000);
    const onFocus = () => fetchSummary();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchSummary]);

  // Reset the P-wait dismissal once the order leaves the waiting state.
  useEffect(() => {
    if (maintenance?.state !== 'waiting') setPwaitDismissed(false);
  }, [maintenance?.state]);

  // Day-only orgs don't track work-order clocks → no panel.
  if (config && config.track === 'day') return null;

  const estimateHours = estimateHoursFromJobs(maintenance);
  const loggedSeconds = summary?.logged_seconds || 0;
  const live = summary?.live_sessions || [];
  const contributors = summary?.contributors || [];
  const amIClockedHere = woSession && woSession.maintenance === maintenance?.id;

  const handleStart = () => {
    if (woSession && woSession.maintenance !== maintenance.id) {
      ctrl.requestSwitch(maintenance.id, {
        fromReg: woSession.maintenance_info?.vehicle_registration,
        toReg: maintenance.vehicle?.registration_number,
        fromMaintenanceId: woSession.maintenance,
      });
    } else {
      ctrl.startWo(maintenance.id);
    }
  };

  const onPick = (m) => {
    setPickerOpen(false);
    ctrl.requestSwitch(m.id, {
      fromReg: woSession?.maintenance_info?.vehicle_registration,
      toReg: m.vehicle?.registration_number,
      fromMaintenanceId: woSession?.maintenance,
    });
  };

  // P-wait (§7.2): only when auto-pause is OFF and a clock is live on this now-waiting order.
  // (With auto-pause ON the backend pauses on commit and the session vanishes on the next poll.)
  const showPWait =
    maintenance?.state === 'waiting' &&
    config?.auto_pause_on_waiting === false &&
    (summary?.live_sessions?.length || 0) > 0 &&
    !pwaitDismissed;

  const setAutoPauseConfig = async () => {
    const orgId = getOrganizationId();
    try {
      await axios.patch(`/api/scheduling/timeclock/config/?organization=${orgId}`, { auto_pause_on_waiting: true });
      fetchConfig();
    } catch {
      /* admin-only; ignore failures */
    }
  };

  const handlePwaitPause = async (auto) => {
    for (const s of summary?.live_sessions || []) {
      await ctrl.clockOutSession(s.id); // foreman+ may clock out others; own always allowed
    }
    if (auto && isAdmin) await setAutoPauseConfig();
    setPwaitDismissed(true);
    fetchSummary();
  };

  const handlePwaitKeep = async (auto) => {
    if (auto && isAdmin) await setAutoPauseConfig();
    setPwaitDismissed(true);
  };

  return (
    <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${TOKENS.border}`, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimerIcon sx={{ color: TOKENS.accent }} />
          <Typography sx={{ fontWeight: 600 }}>{t('worktime.panel.title')}</Typography>
        </Box>
        <Typography sx={{ fontSize: 13, color: TOKENS.muted }}>
          {estimateHours != null && (
            <>
              {t('worktime.panel.estimate')} <b style={{ color: TOKENS.fg }}>{hmShort(estimateHours * 3600, t)}</b> ·{' '}
            </>
          )}
          {t('worktime.panel.logged')} <b style={{ color: TOKENS.fg }}>{hmShort(loggedSeconds, t)}</b>
        </Typography>
      </Box>

      <Stack spacing={1} sx={{ mb: 2 }}>
        {live.map((s) => (
          <Box
            key={s.id}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 2, backgroundColor: TOKENS.greenSoft, border: `1px solid rgba(22,163,74,0.2)` }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: TOKENS.green, fontSize: 14 }}>{initials(s.employee_name)}</Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: TOKENS.fg }} noWrap>
                {s.employee_name}
              </Typography>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: TOKENS.greenDark }}>
                <LiveDot size={7} /> {t('worktime.panel.clockingNow')}
              </Box>
            </Box>
            <Box sx={{ ...numFont, fontSize: 16, fontWeight: 700, color: TOKENS.greenDark }}>
              {hmShort(elapsedSeconds(s.started_at, nowMs), t)}
            </Box>
          </Box>
        ))}

        {contributors.map((c) => (
          <Box key={c.employee_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 2, backgroundColor: TOKENS.bg }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: TOKENS.subtle, fontSize: 13 }}>{initials(c.employee_name)}</Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, color: TOKENS.fg }} noWrap>
                {c.employee_name}
              </Typography>
              {c.last_at && <Typography sx={{ fontSize: 12, color: TOKENS.subtle }}>{fmtDate(c.last_at)}</Typography>}
            </Box>
            <Box sx={{ ...numFont, fontSize: 14, color: TOKENS.muted }}>{hmShort(c.total_seconds, t)}</Box>
          </Box>
        ))}

        {live.length === 0 && contributors.length === 0 && (
          <Typography sx={{ fontSize: 13, color: TOKENS.subtle }}>{t('worktime.panel.noTimeYet')}</Typography>
        )}
      </Stack>

      {amIClockedHere ? (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<CheckCircleIcon />}
          onClick={() => ctrl.clockOutSession(woSession.id)}
          disabled={ctrl.busy}
          sx={{ color: TOKENS.red, borderColor: TOKENS.redBorder }}
        >
          {t('worktime.onWo.endThis')}
        </Button>
      ) : (
        <>
          <Button
            fullWidth
            startIcon={<PlayArrowIcon />}
            onClick={handleStart}
            disabled={ctrl.busy}
            sx={{ backgroundColor: TOKENS.green, color: '#fff', '&:hover': { backgroundColor: TOKENS.greenDark } }}
          >
            {t('worktime.panel.startCta')}
          </Button>
          {config?.auto_start_in_progress !== false && maintenance?.state !== 'in_progress' && (
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 1.25, fontSize: 12, color: TOKENS.subtle }}>
              <BoltIcon sx={{ fontSize: 14, color: TOKENS.amber }} />
              <span>{t('worktime.panel.autoStartHint')}</span>
            </Box>
          )}
        </>
      )}

      {ctrl.dialogs}
      <PWaitDialog open={showPWait} canSetAuto={isAdmin} busy={ctrl.busy} onPause={handlePwaitPause} onKeep={handlePwaitKeep} />
      <WorkOrderPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={onPick} excludeMaintenanceId={maintenance?.id} />
    </Paper>
  );
}
